/**
 * The publish gate dialog — `openPublishGate`, `m08-admin.js:113`.
 *
 * The whole system has one of these. The wizard, the asset library and the template list all
 * produce drafts and route the user here; this dialog is the only place a `TemplateRelease` is
 * cut. The checklist it draws and the publication it performs both live in `lib/publish-gate.ts`,
 * so this file is markup, the explicit confirmation and the write — and imports nothing from the
 * editor screen, which imports it.
 *
 * Two behaviours are the reference's and look like faults until you read it:
 *
 *   - the checklist is computed ONCE, when the dialog opens (`const cl = releaseChecklist(...)`
 *     before `openModal`). Ticking the confirmation box does not redraw the `ack` row, so that row
 *     keeps showing ✕ next to a ticked box. `sync` compensates by excluding `ack` from the rows it
 *     requires, which is why the box alone enables the button.
 *   - `preview` is a BLOCKING row and nothing in the editor sets `__previewChecked`, so for a
 *     template the demo seed published the button cannot enable at all. See the note on that row
 *     in `lib/publish-gate.ts`.
 */
import { type ReactElement, useState } from 'react';
import { useV07Commands } from '../../data/commands-context';
import { useSettings } from '../../data/store';
import type { DemoRelease, DemoTemplate } from '../../data/types';
import { applyRelease, buildRelease, type ChecklistItem, releaseChecklist } from '../../lib/publish-gate';
import { Modal } from '../../ui/Modal';
import { useToast } from '../../ui/toast';
import type { EditorLayout } from './contract';

export interface PublishGateProps {
  template: DemoTemplate;
  /** The layout being published: the editor's working copy, not the template's stored draft. */
  layout: EditorLayout;
  onClose: () => void;
  /** `after(res.release)` upstream — called once the release is in the database. */
  onPublished: (release: DemoRelease) => void;
  /**
   * Whether the administrator opened and closed the editor's preview in this session.
   *
   * The `preview` row is BLOCKING and nothing in the reference's editor sets `__previewChecked`, so
   * a template the product created could never pass the gate — see the note in `EditorScreen`. The
   * demo's own templates carry the flag on the record and are unaffected either way.
   */
  previewReviewed?: boolean | undefined;
}

function ChecklistRow({ item }: { item: ChecklistItem }): ReactElement {
  const state = item.ok ? 'is-ok' : item.blocking ? 'is-bad' : 'is-warn';
  const icon = item.ok ? '✓' : item.blocking ? '✕' : '!';
  return (
    <div className={`chk-row ${state}`}>
      <span className="ico">{icon}</span>
      <div>
        <b>{item.label}</b>
        {item.detail ? <span className="small muted">{item.detail}</span> : null}
      </div>
      {item.blocking ? null : <span className="pill pill-neutral none">bloklamaz</span>}
    </div>
  );
}

export function PublishGate({
  template,
  layout,
  onClose,
  onPublished,
  previewReviewed,
}: PublishGateProps): ReactElement {
  const settings = useSettings();
  const commands = useV07Commands();
  const toast = useToast();

  /* Frozen at open, as upstream: the rows are rendered from this one evaluation and never from a
     later one, so the `ack` row does not flip while the box is being ticked. */
  const [checklist] = useState(() =>
    releaseChecklist(previewReviewed === true ? { ...template, __previewChecked: true } : template, layout),
  );
  const [note, setNote] = useState(template.releases.length ? 'Düzenleme yayınlandı' : 'İlk yayın');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  /* `sync` — `m08-admin.js:128`. `ack` is excluded by key because the frozen checklist still
     carries the value it had before the box was ticked. */
  const gatePassed = checklist.items.filter((i) => i.blocking && i.key !== 'ack').every((i) => i.ok);

  const publish = async (): Promise<void> => {
    setBusy(true);
    /* Upstream writes `t.__publishAck = true` on the record immediately before publishing, because
       `publishRelease` runs the checklist again and the `ack` row has to pass there too. */
    const acked = { ...template, __publishAck: true, ...(previewReviewed ? { __previewChecked: true } : {}) };
    const res = await buildRelease(acked, layout, note.trim() || 'Yayın', { settings });
    if (!res.ok) {
      setBusy(false);
      setProblems(res.problems);
      return;
    }
    /*
     * The publish is the backend's own act: `template.publish_release` runs the gate again
     * server-side and freezes the release there, immutably (rule 3). The local apply below is what
     * the visual acceptance demo does with its own store; in the product it is overwritten by the
     * refresh the command performs, so the screen shows the release the server actually minted.
     */
    const published = await commands.publishRelease({ templateId: template.id });
    if (!published.ok) {
      setBusy(false);
      setProblems([published.message ?? 'Yayınlanamadı']);
      return;
    }
    /*
     * Whose release is on screen afterwards.
     *
     * When a backend answered, it minted the release and `publishRelease` already re-read the world
     * — the store holds the real one, with the real uuid. Writing the locally built `res.release`
     * on top of it replaced that uuid with `<templateId>-r<n>`, and the next single issuance sent
     * THAT as `templateReleaseId`: «Şablon sürümü — biçimi geçersiz», until a page reload discarded
     * the invention. So the local apply is the demo's write and only the demo's; the product reports
     * the number and hash the server answered with.
     */
    const minted = published.release ?? null;
    if (minted === null)
      commands.patchLocal((db) => {
        const t = db.templates.find((x) => x.id === template.id);
        if (t) applyRelease(t, res);
      });
    onClose();
    const shown = minted ?? res.release;
    toast(`${template.name} v${shown.releaseNumber} yayınlandı — hash ${shown.releaseHash.slice(0, 12)}…`);
    onPublished(res.release);
  };

  return (
    <Modal label="Yayın kapısı" wide onClose={onClose}>
      {/* `ModalHead` renders a bare `<h2>`; this head carries the «Yayın kapısı» eyebrow above the
          title, so it is written out rather than losing that line of the reference. */}
      <div className="card-head">
        <div>
          <span className="eyebrow">Yayın kapısı</span>
          <h2 style={{ marginTop: '6px' }}>{`${template.name} — release yayınla`}</h2>
        </div>
        <button className="icon-btn" type="button" data-close="1" aria-label="Kapat" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="card-pad stack" style={{ gap: '12px' }}>
        <div className="info-box">
          {'Yayınlanan release '}
          <b>immutable</b>
          {
            "'dır: anlam, policy, alan şeması ve render sözleşmesi bu anda dondurulur. Daha önce üretilmiş kayıtlar kendi release snapshot'larında kalır; kök şablonda yapılan değişiklikler onları etkilemez."
          }
        </div>

        <div className="chk-list">
          {checklist.items.map((item) => (
            /*
             * Frozen at open — except the one row the box in this dialog decides.
             *
             * The evaluation is deliberately taken once, so a row cannot flip while the author is
             * reading it. But `ack` is not a fact about the design; it is a fact about THIS dialog,
             * and it was read before the box existed to tick. So the row sat red with the box
             * plainly checked, which is the checklist telling the author something untrue about
             * what they had just done. Only this row is re-read, and only from the box.
             */
            <ChecklistRow
              item={item.key === 'ack' ? { ...item, ok: ack, detail: ack ? '' : item.detail } : item}
              key={item.key}
            />
          ))}
        </div>

        <div className="field">
          <label htmlFor="pgNote">Release notu</label>
          <input
            className="input"
            id="pgNote"
            value={note}
            maxLength={140}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <label className="check">
          <input
            type="checkbox"
            id="pgAck"
            checked={ack}
            onChange={(e) => {
              setAck(e.target.checked);
              /* The reference stores the answer on the template record, so reopening the gate
                 shows the `ack` row already satisfied (`m08-admin.js:129`). */
              /* Screen state, never sent: the gate's own «I read it» marker (§11's local list). */
              commands.patchLocal((db) => {
                const t = db.templates.find((x) => x.id === template.id);
                if (t) t.__publishAck = e.target.checked;
              });
            }}
          />
          {" Kontrol listesini inceledim ve bu release'i yayınlamayı açıkça onaylıyorum."}
        </label>

        <div id="pgResult">
          {problems.length ? (
            <div className="warn-box">
              <b>Yayınlanamadı.</b>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>

      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={onClose}>
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          id="pgGo"
          type="button"
          disabled={busy || !(ack && gatePassed)}
          onClick={() => {
            /*
             * A publish that THROWS must not leave the dialog pretending to work.
             *
             * `publish` reports every refusal it can see, but an exception on the way — a read that
             * rejects, a projection that trips over an unexpected shape — escaped as an unhandled
             * rejection: `busy` stayed true, the result box stayed empty, and the gate sat there
             * with nothing said and no way forward. That is the one outcome this dialog must never
             * produce, whatever went wrong.
             */
            publish().catch((e: unknown) => {
              setBusy(false);
              setProblems([e instanceof Error ? e.message : 'Yayınlanamadı']);
            });
          }}
        >
          Release'i yayınla
        </button>
      </div>
    </Modal>
  );
}
